import type { PathwayConditions, PathwayDimension } from "@app/types/dashboard-pathway";
import type { NLFilterCondition, NLSearchBase } from "@app/zustand/store";

import { type BoardPeriod, cleanRange, conditionsKey, selectionKey } from "./conditions";
import { searchPeriod } from "./usePathway";

/*
 * The natural-language search's answer, as the board takes it (PT-146): the conditions and period it sets,
 * and how it lands on a board the reader may have edited while the model answered. Pure, so the bridge in
 * `PathwayBody` only decides WHEN to apply it.
 */

/** What a search's answer sets on this board: its conditions, its period as the dashboard holds it (a
 * token, or a window's two days), and the columns this board cannot express. */
export interface SearchBoard {
  conditions: PathwayConditions;
  period: BoardPeriod;
  ignored: string[];
}

export function searchBoard(
  nlFilters: NLFilterCondition[],
  byKey: Map<string, PathwayDimension>,
): SearchBoard {
  const conditions: PathwayConditions = {};
  const ignored: string[] = [];
  let start: string | null = null;
  let end: string | null = null;
  let token: string | null = null;
  for (const condition of nlFilters) {
    const values = condition.values ?? (condition.value != null ? [String(condition.value)] : []);
    const single = values[0] ?? null;

    /* TIME first, and it is not a dimension: the period — the patients SEEN in a window — comes as the
       virtual columns `periode` / `periode_start` / `periode_end`, which the pathway routes read as query
       parameters. A period that dates the act is a condition on the act's date axis instead (PT-146). */
    if (condition.column === "periode_start") {
      start = single;
      continue;
    }
    if (condition.column === "periode_end") {
      end = single;
      continue;
    }
    if (condition.column === "periode" && single) {
      const period = searchPeriod(single);
      if (period === null) ignored.push(condition.column);
      else if ("token" in period) token = period.token;
      else ({ start, end } = period);
      continue;
    }
    // A practitioner condition is dropped ON PURPOSE and named like any other: under HDS a reader
    // may not narrow to what a named colleague does. Admins and reviewers, whose pool already spans
    // those colleagues, narrow through the rail, on an account rather than on a name read in prose.
    if (condition.column === "praticien") {
      ignored.push(condition.column);
      continue;
    }

    /* The answer names this board's own axes: the search's vocabulary is built from them, whatever the
       speciality. A column this catalogue does not hold can still come back — a condition the board
       carried on a field since withdrawn or withheld from this reader, the demo's canned answer — and it
       must be NAMED: dropping it silently would show a narrowed board under a search that asked for
       something else. */
    if (!byKey.has(condition.column)) {
      ignored.push(condition.column);
      continue;
    }
    /* A date or a measure asked for by its value (PT-146): a range on that axis, where the axis
       takes one — the same condition the rail's « de … à … » writes. */
    if (condition.op === "range") {
      const dim = byKey.get(condition.column);
      const range = cleanRange({ min: condition.min, max: condition.max });
      if (dim?.ranges && range) conditions[condition.column] = range;
      else ignored.push(condition.column);
      continue;
    }
    // Otherwise a condition here is "carries one of these labels". An inequality or a substring is
    // not that, and read as an equality it answers another question — so it is named too.
    if (condition.op !== "eq" && condition.op !== "array_contains") {
      ignored.push(condition.column);
      continue;
    }
    if (values.length > 0) conditions[condition.column] = values.map(String);
  }
  return {
    conditions,
    ignored,
    period: token
      ? { periode: token, periodeStart: null, periodeEnd: null }
      : { periode: null, periodeStart: start, periodeEnd: end },
  };
}

/**
 * The answer over the board as it is now rather than as it was when asked: an axis — or the period — the
 * answer left as it found it keeps whatever the reader set on it while the model answered. With no edit
 * in between, this is the answer itself.
 */
export function rebased(
  answer: SearchBoard,
  base: NLSearchBase,
  current: { conditions: PathwayConditions; period: BoardPeriod },
): { conditions: PathwayConditions; period: BoardPeriod } {
  const keyOf = (all: PathwayConditions, key: string) =>
    all[key] === undefined ? "" : conditionsKey({ [key]: all[key] });
  const conditions: PathwayConditions = {};
  const axes = new Set([
    ...Object.keys(base.conditions),
    ...Object.keys(answer.conditions),
    ...Object.keys(current.conditions),
  ]);
  for (const key of axes) {
    const touched = keyOf(answer.conditions, key) !== keyOf(base.conditions, key);
    const held = touched ? answer.conditions[key] : current.conditions[key];
    if (held !== undefined) conditions[key] = held;
  }
  const touched = selectionKey({}, answer.period) !== selectionKey({}, base);
  return { conditions, period: touched ? answer.period : current.period };
}
